import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toastError, toastSuccess } from "../utils/toast";
import { apiPost } from "../api";
import { safePostLoginPath, saveSession } from "../utils/authSession";
import "./css/login.css";

const Eye = (props) => (
  <svg
    viewBox="0 0 24 24"
    width="18"
    height="18"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOff = (props) => (
  <svg
    viewBox="0 0 24 24"
    width="18"
    height="18"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M17.94 17.94A10.94 10.94 0 0 1 12 19c-7 0-11-7-11-7a20.29 20.29 0 0 1 5.06-5.94M9.88 9.88A3 3 0 0 0 12 15a3 3 0 0 0 2.12-.88M1 1l22 22" />
  </svg>
);

// Minimal circular spinner (monochrome: inherits currentColor)
const CircularProgress = ({ size = 16, stroke = 2 }) => (
  <svg
    className="auth-spinner"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <circle
      cx="12"
      cy="12"
      r="9"
      fill="none"
      stroke="currentColor"
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeDasharray="56"
      strokeDashoffset="28"
    />
  </svg>
);

export default function Login() {
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    const { email, password } = e.currentTarget;

    const payload = {
      email: email.value.trim(),
      password: password.value,
    };

    // ✅ Both fields required
    if (!payload.email || !payload.password) {
      toastError("Email and password are required.");
      return;
    }

    try {
      setLoading(true);
      const res = await apiPost("user/signin", payload);

      if (res.data?.status === "Inactive") {
        toastError("Your account is inactive. Please contact the administrator.");
        return;
      }

      const { id, name, email: userEmail, role, status, image } = res.data;
      const token = res.token;

      saveSession({
        id,
        name,
        email: userEmail,
        role,
        status,
        image,
        token,
        expires_at: res.expires_at || null,
      });

      toastSuccess("Login successful");

      const next = new URLSearchParams(window.location.search).get("next");
      navigate(safePostLoginPath(next, role));
    } catch (error) {
      toastError(error?.response?.data?.message || error.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="auth-wrap">
      <form
        className="auth-card"
        onSubmit={onSubmit}
        noValidate
        aria-busy={loading}
      >
        <p className="cl-eyebrow">Account</p>
        <h1 className="auth-title">Login</h1>

        <label className="auth-label">
          <span className="req" aria-hidden="true">*</span> Email
          <input
            type="email"
            name="email"
            className="auth-input"
            placeholder="Email"
            autoComplete="email"
            required
            disabled={loading}
          />
        </label>

        <label className="auth-label">
          <span className="req" aria-hidden="true">*</span> Password
          <div className="auth-input-pass">
            <input
              type={show ? "text" : "password"}
              name="password"
              className="auth-input"
              placeholder="Password"
              autoComplete="current-password"
              required
              disabled={loading}
            />
            <button
              type="button"
              className="pass-toggle"
              onClick={() => setShow((s) => !s)}
              aria-label={show ? "Hide password" : "Show password"}
              disabled={loading}
            >
              {show ? <EyeOff /> : <Eye />}
            </button>
          </div>
        </label>

        <button className="auth-btn" type="submit" disabled={loading}>
          {loading ? (
            <>
              <CircularProgress />
              <span>Logging in…</span>
            </>
          ) : (
            "Login"
          )}
        </button>
      </form>
    </section>
  );
}
